import { X, MapPin, Calendar, User, Package, Truck, Store, ExternalLink, Image as ImageIcon } from 'lucide-react';
import { ProductDetails } from '../types';

interface ProductDetailsModalProps {
  product: ProductDetails;
  onClose: () => void;
}

export function ProductDetailsModal({ product, onClose }: ProductDetailsModalProps) {
  const allImages = [
    ...(product.images || []),
    ...(product.additional_images || [])
  ].filter(Boolean);

  const marketplaceColors = {
    FBM: 'bg-blue-500',
    OfferUp: 'bg-green-500',
    Amazon: 'bg-orange-500',
    Craigslist: 'bg-purple-500',
    eBay: 'bg-red-500',
  };

  const marketplaceLabels = {
    FBM: 'Facebook Marketplace',
    OfferUp: 'OfferUp',
    Amazon: 'Amazon',
    Craigslist: 'Craigslist',
    eBay: 'eBay',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            {product.marketplace && (
              <div className={`px-3 py-1 rounded-lg text-xs font-semibold text-white ${marketplaceColors[product.marketplace]}`}>
                {marketplaceLabels[product.marketplace]}
              </div>
            )}
            <h2 className="text-lg sm:text-xl font-bold text-neutral-900 dark:text-neutral-100">
              Product Details
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-neutral-600 dark:text-neutral-400" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Images Section */}
            <div className="space-y-4">
              {allImages.length > 0 ? (
                <>
                  <div className="relative aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-700">
                    <img
                      src={allImages[0]}
                      alt={product.title || 'Product image'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      width="600"
                      height="600"
                    />
                  </div>
                  {allImages.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {allImages.slice(1, 5).map((img, idx) => (
                        <div
                          key={idx}
                          className="relative aspect-square rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-700 cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          <img
                            src={img}
                            alt={`${product.title || 'Product'} - Image ${idx + 2}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            width="150"
                            height="150"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="aspect-square rounded-lg bg-neutral-100 dark:bg-neutral-700 flex items-center justify-center">
                  <ImageIcon size={48} className="text-neutral-400" />
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="space-y-4">
              {/* Title */}
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                  {product.title}
                </h3>
                {product.price !== null && (
                  <div className="text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                    ${product.price.toLocaleString()}
                    {product.currency && product.currency !== 'USD' && (
                      <span className="text-lg text-neutral-500 dark:text-neutral-400 ml-2">
                        {product.currency}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Description
                  </h4>
                  <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-4">
                {product.condition && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                      Condition
                    </div>
                    <div className="text-sm text-neutral-900 dark:text-neutral-100">
                      {product.condition}
                    </div>
                  </div>
                )}

                {product.location && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <MapPin size={12} />
                      Location
                    </div>
                    <div className="text-sm text-neutral-900 dark:text-neutral-100">
                      {product.location}
                    </div>
                  </div>
                )}

                {product.date_posted && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Calendar size={12} />
                      Date Posted
                    </div>
                    <div className="text-sm text-neutral-900 dark:text-neutral-100">
                      {new Date(product.date_posted).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                )}

                {product.availability && (
                  <div>
                    <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">
                      Availability
                    </div>
                    <div className="text-sm text-neutral-900 dark:text-neutral-100">
                      {product.availability}
                    </div>
                  </div>
                )}
              </div>

              {/* Seller Info */}
              {product.seller_info && (product.seller_info.name || product.seller_info.rating) && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center gap-1">
                    <User size={12} />
                    Seller Information
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-neutral-100">
                    {product.seller_info.name && (
                      <div className="font-medium">{product.seller_info.name}</div>
                    )}
                    {product.seller_info.type && (
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                        Type: {product.seller_info.type}
                      </div>
                    )}
                    {product.seller_info.rating && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-yellow-500">⭐</span>
                        <span className="font-medium">{product.seller_info.rating.toFixed(1)}</span>
                        {product.seller_info.review_count && (
                          <span className="text-xs text-neutral-500 dark:text-neutral-400">
                            ({product.seller_info.review_count} reviews)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Shipping/Pickup Info */}
              {(product.shipping_info || product.pickup_info) && (
                <div className="space-y-3">
                  <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                    Delivery Options
                  </div>
                  {product.shipping_info && (
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <Truck size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-blue-900 dark:text-blue-300">
                          Shipping Available
                        </div>
                        {typeof product.shipping_info === 'object' && (
                          <div className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                            {JSON.stringify(product.shipping_info, null, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {product.pickup_info && (
                    <div className="flex items-start gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <Store size={16} className="text-green-600 dark:text-green-400 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-green-900 dark:text-green-300">
                          Pickup Available
                        </div>
                        {typeof product.pickup_info === 'object' && (
                          <div className="text-xs text-green-700 dark:text-green-400 mt-1">
                            {JSON.stringify(product.pickup_info, null, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Attributes */}
              {product.attributes && Object.keys(product.attributes).length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center gap-1">
                    <Package size={12} />
                    Attributes
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(product.attributes).map(([key, value]) => (
                      <div key={key} className="p-2 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
                        <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase">
                          {key.replace(/_/g, ' ')}
                        </div>
                        <div className="text-sm text-neutral-900 dark:text-neutral-100 mt-1">
                          {String(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-neutral-200 dark:border-neutral-700 p-4 sm:p-6 flex gap-3">
          <a
            href={product.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 btn-primary py-3 flex items-center justify-center gap-2"
          >
            <ExternalLink size={18} />
            View on {marketplaceLabels[product.marketplace] || product.marketplace}
          </a>
          <button
            onClick={onClose}
            className="px-6 py-3 btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

